import { ProactivityState, ReportQuery, Type } from './Interfaces';
import { openSnackbar } from '../AppSnackbar/snackbarSlice';

export function encodeComplexityQuery(min: number, max: number): string {
  const complexities: number[] = [];
  for (let i = min; i <= max; i++) {
    complexities.push(i);
  }

  const jsonString: string = JSON.stringify(complexities);

  const encodedString: string = encodeURIComponent(jsonString);

  return `complexity=${encodedString}`;
}

export function encodeServiceTypeQuery(serviceTypes: string[]): string {
  if (serviceTypes.length === 0) {
    return '';
  }

  const jsonString: string = JSON.stringify(serviceTypes);
  const encodedString: string = encodeURIComponent(jsonString);

  return `&type=${encodedString}`;
}

export function encodeUnitTypeQuery(unitTypes: string[]): string {
  if (unitTypes.length === 0) {
    return '';
  }

  const jsonString: string = JSON.stringify(unitTypes);
  const encodedString: string = encodeURIComponent(jsonString);

  return `&units=${encodedString}`;
}

export function encodeTopicQuery(topics: string[]): string {
  if (topics.length === 0) {
    return '';
  }

  const jsonString: string = JSON.stringify(topics);
  const encodedString: string = encodeURIComponent(jsonString);

  return `&topics=${encodedString}`;
}

export function encodeProactivityQuery(proactivity: ProactivityState): string {
  const proactivityState = [];
  if (proactivity.proactive) {
    proactivityState.push('proactive');
  }
  if (proactivity.reactive) {
    proactivityState.push('reactive');
  }

  if (proactivityState.length === 0) {
    return '';
  }

  const jsonString: string = JSON.stringify(proactivityState);
  const encodedString: string = encodeURIComponent(jsonString);

  return `&proactivity=${encodedString}`;
}

export function encodeReportQuery(query: ReportQuery): string {
  return `startDate=${query.startDate}&endDate=${query.endDate}&${encodeComplexityQuery(
    query.minComplexity,
    query.maxComplexity
  )}${encodeServiceTypeQuery(query.selectedServiceTypes.map((s) => s._id))}&includeKeyMessaging=${
    query.includeKeyMessaging ? 'true' : 'false'
  }&includeBackgroundResponse=${query.includeBackgroundResponse ? 'true' : 'false'}&includeMetadata=${
    query.includeMetadata ? 'true' : 'false'
  }${encodeProactivityQuery(query.proactivityState)}${encodeUnitTypeQuery(
    query.selectedUnitTypes.map((u) => u.name)
  )}${encodeTopicQuery(query.selectedTopics)}`;
}

export function isValidEmail(email: string): boolean {
  const regex = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,}$/;
  return regex.test(email);
}

export function filterTypesByProactivity(
  type: Type,
  proactivityState: { reactive: boolean; proactive: boolean }
): boolean {
  return (
    (type.proactivity === 'Reactive' && proactivityState.reactive) ||
    (type.proactivity === 'Proactive' && proactivityState.proactive)
  );
}

export const sortTypesAlphabetically = <T>(types: T[], typeName: keyof T): T[] => {
  return [...types].sort((a, b) => {
    const nameA = String(a[typeName]).toLowerCase();
    const nameB = String(b[typeName]).toLowerCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  });
};

export const handleEmailResponse = (emailResponse: any, email: string, dispatch: any) => {
  if (emailResponse.error) {
    if (emailResponse.status === 404) {
      dispatch(
        openSnackbar({
          severity: 'error',
          message: 'Email failed to send, no data was found! Try using different filters',
        })
      );
    } else {
      dispatch(
        openSnackbar({
          severity: 'error',
          message: 'Email failed to send, the payload is too large! Try narrowing your dates or using more filters!',
        })
      );
    }
    return;
  }

  dispatch(
    openSnackbar({
      severity: 'success',
      message:
        'Email sent! Check your inbox at ' +
        email +
        ' for the report. If you have not received the report, kindly check your spam folder.',
    })
  );
};

export const triggerErrorDispatch = (
  dispatch: any,
  proactivityState: {
    proactive: boolean;
    reactive: boolean;
  },
  email: string
): boolean => {
  if (!proactivityState.proactive && !proactivityState.reactive) {
    dispatch(openSnackbar({ severity: 'error', message: 'Please enter at least one option for proactivity!' }));
    return true;
  }
  if (email === '') {
    dispatch(openSnackbar({ severity: 'error', message: 'Please enter your E-mail first!' }));
    return true;
  } else if (!isValidEmail(email)) {
    dispatch(openSnackbar({ severity: 'error', message: 'Please enter a valid E-mail!' }));
    return true;
  }

  return false;
};
